import React, { forwardRef } from "react";
import { TypographyBase, TypographyProps } from "./TypographyBase";
import { cn } from "../../styles";

/** Typography for Span elements. */
export interface TypographySpanProps
  extends TypographyProps,
    Omit<
      React.HTMLAttributes<HTMLSpanElement>,
      "color" | "prefix" | "content"
    > {}

export const TypographySpan = forwardRef<HTMLSpanElement, TypographySpanProps>(
  (p, ref) => {
    return <TypographyBase {...p} ref={ref} render={(p) => <span {...p} />} />;
  }
);

/** Typography for Label elements. */
export interface TypographyLabelProps
  extends TypographyProps,
    Omit<
      React.LabelHTMLAttributes<HTMLLabelElement>,
      "color" | "prefix" | "content"
    > {}

export const TypographyLabel = forwardRef<
  HTMLLabelElement,
  TypographyLabelProps
>((p, ref) => {
  return <TypographyBase {...p} ref={ref} render={(p) => <label {...p} />} />;
});

/** Typography for H1 elements. */
export interface TypographyH1Props
  extends TypographyProps,
    Omit<
      React.HTMLAttributes<HTMLHeadingElement>,
      "color" | "prefix" | "content"
    > {}

export const TypographyH1 = forwardRef<HTMLHeadingElement, TypographyH1Props>(
  (p, ref) => {
    return (
      <TypographyBase
        {...p}
        ref={ref}
        className={cn("text-3xl font-bold mb-4", p.className)}
        render={(p) => <h1 {...p} />}
      />
    );
  }
);

/** Typography for H2 elements. */
export interface TypographyH2Props
  extends TypographyProps,
    Omit<
      React.HTMLAttributes<HTMLHeadingElement>,
      "color" | "prefix" | "content"
    > {}

export const TypographyH2 = forwardRef<HTMLHeadingElement, TypographyH2Props>(
  (p, ref) => {
    return (
      <TypographyBase
        {...p}
        ref={ref}
        className={cn("text-2xl font-semibold mb-3", p.className)}
        render={(p) => <h2 {...p} />}
      />
    );
  }
);

/** Typography for H3 elements. */
export interface TypographyH3Props
  extends TypographyProps,
    Omit<
      React.HTMLAttributes<HTMLHeadingElement>,
      "color" | "prefix" | "content"
    > {}

export const TypographyH3 = forwardRef<HTMLHeadingElement, TypographyH3Props>(
  (p, ref) => {
    return (
      <TypographyBase
        {...p}
        ref={ref}
        className={cn("text-xl font-semibold mb-3", p.className)}
        render={(p) => <h3 {...p} />}
      />
    );
  }
);

/** Typography for H4 elements. */
export interface TypographyH4Props
  extends TypographyProps,
    Omit<
      React.HTMLAttributes<HTMLHeadingElement>,
      "color" | "prefix" | "content"
    > {}

export const TypographyH4 = forwardRef<HTMLHeadingElement, TypographyH4Props>(
  (p, ref) => {
    return (
      <TypographyBase
        {...p}
        ref={ref}
        className={cn("text-lg font-medium mb-2", p.className)}
        render={(p) => <h4 {...p} />}
      />
    );
  }
);

/** Typography for H5 elements. */
export interface TypographyH5Props
  extends TypographyProps,
    Omit<
      React.HTMLAttributes<HTMLHeadingElement>,
      "color" | "prefix" | "content"
    > {}

export const TypographyH5 = forwardRef<HTMLHeadingElement, TypographyH5Props>(
  (p, ref) => {
    return (
      <TypographyBase
        {...p}
        ref={ref}
        className={cn("text-base font-medium mb-2", p.className)}
        render={(p) => <h5 {...p} />}
      />
    );
  }
);

/** Typography for H6 elements. */
export interface TypographyH6Props
  extends TypographyProps,
    Omit<
      React.HTMLAttributes<HTMLHeadingElement>,
      "color" | "prefix" | "content"
    > {}

export const TypographyH6 = forwardRef<HTMLHeadingElement, TypographyH6Props>(
  (p, ref) => {
    return (
      <TypographyBase
        {...p}
        ref={ref}
        className={cn("text-sm font-medium mb-2", p.className)}
        render={(p) => <h6 {...p} />}
      />
    );
  }
);

/** Typography for P elements. */
export interface TypographyPProps
  extends TypographyProps,
    Omit<
      React.HTMLAttributes<HTMLParagraphElement>,
      "color" | "prefix" | "content"
    > {}

export const TypographyP = forwardRef<HTMLParagraphElement, TypographyPProps>(
  (p, ref) => {
    return (
      <TypographyBase
        {...p}
        ref={ref}
        className={cn("text-base leading-relaxed mb-4", p.className)}
        render={(p) => <p {...p} />}
      />
    );
  }
);

/** Typography for Small elements. */
export interface TypographySmallProps
  extends TypographyProps,
    Omit<React.HTMLAttributes<HTMLElement>, "color" | "prefix" | "content"> {}

export const TypographySmall = forwardRef<HTMLElement, TypographySmallProps>(
  (p, ref) => {
    return (
      <TypographyBase
        {...p}
        ref={ref}
        className={cn("text-sm text-front-600", p.className)}
        render={(p) => <small {...p} />}
      />
    );
  }
);

/** Typography for Strong elements. */
export interface TypographyStrongProps
  extends TypographyProps,
    Omit<React.HTMLAttributes<HTMLElement>, "color" | "prefix" | "content"> {}

export const TypographyStrong = forwardRef<HTMLElement, TypographyStrongProps>(
  (p, ref) => {
    return (
      <TypographyBase
        {...p}
        ref={ref}
        className={cn("font-bold", p.className)}
        render={(p) => <strong {...p} />}
      />
    );
  }
);

/** Typography for Em elements. */
export interface TypographyEmProps
  extends TypographyProps,
    Omit<React.HTMLAttributes<HTMLElement>, "color" | "prefix" | "content"> {}

export const TypographyEm = forwardRef<HTMLElement, TypographyEmProps>(
  (p, ref) => {
    return (
      <TypographyBase
        {...p}
        ref={ref}
        className={cn("italic", p.className)}
        render={(p) => <em {...p} />}
      />
    );
  }
);

/** Typography for Code elements. */
export interface TypographyCodeProps
  extends TypographyProps,
    Omit<React.HTMLAttributes<HTMLElement>, "color" | "prefix" | "content"> {}

export const TypographyCode = forwardRef<HTMLElement, TypographyCodeProps>(
  (p, ref) => {
    return (
      <TypographyBase
        {...p}
        ref={ref}
        className={cn(
          "font-mono text-xs bg-back-200 dark:bg-back-700 px-1.5 py-0.5 rounded border border-back-300 dark:border-back-600 text-front-800 dark:text-front-200",
          p.className
        )}
        render={(p) => <code {...p} />}
      />
    );
  }
);

/** Typography for Quote elements. */
export interface TypographyQuoteProps
  extends TypographyProps,
    Omit<
      React.BlockquoteHTMLAttributes<HTMLQuoteElement>,
      "color" | "prefix" | "content"
    > {}

export const TypographyQuote = forwardRef<
  HTMLQuoteElement,
  TypographyQuoteProps
>((p, ref) => {
  return (
    <TypographyBase
      {...p}
      ref={ref}
      className={cn(
        "border-l-4 border-primary-300 pl-4 py-2 mb-4 italic text-front-700",
        p.className
      )}
      render={(p) => <blockquote {...p} />}
    />
  );
});

/** Typography for Option elements. */
export interface TypographyOptionProps
  extends TypographyProps,
    Omit<
      React.OptionHTMLAttributes<HTMLOptionElement>,
      "color" | "prefix" | "content"
    > {}

export const TypographyOption = forwardRef<
  HTMLOptionElement,
  TypographyOptionProps
>((p, ref) => {
  return <TypographyBase {...p} ref={ref} render={(p) => <option {...p} />} />;
});
